// frontend\src\pages\FilmForm.js
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { BiArrowBack, BiSave } from "react-icons/bi";
import api from "../api/api";
import { GET_FILMS_API, FILMS_API } from "../constants";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import ScrollToTopButton from "../components/ScrollToTopButton";

const FilmForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlFormat = searchParams.get("format") || "";

  const [film, setFilm] = useState({
    name: "",
    iso: 100,
    format: urlFormat,
    film_type: "negative",
    color_type: "bw",
    quantity: 1,
    price_per_roll: "",
    expiry_date: "",
    notes: "",
  });

  const [filmsList, setFilmsList] = useState([]);
  const [isLoading, setIsLoading] = useState(!!id);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchFilm = useCallback(async () => {
    try {
      const response = await api.get(`${FILMS_API}${id}/`);
      setFilm(response.data);
    } catch (error) {
      console.error("Ошибка загрузки:", error);
      toast.error("Ошибка загрузки данных!");
      navigate("/films");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  const loadFilmsList = useCallback(async () => {
    try {
      const response = await api.get(GET_FILMS_API);
      setFilmsList(response.data || []);
    } catch (error) {
      console.error("Ошибка загрузки списка плёнок:", error);
    }
  }, []);

  useEffect(() => {
    loadFilmsList();
    if (id) {
      fetchFilm();
    } else {
      setIsLoading(false);
    }
  }, [id, loadFilmsList, fetchFilm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilm((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    try {
      if (id) {
        await api.put(`${FILMS_API}${id}/`, film);
        toast.success("Данные успешно обновлены!");
      } else {
        await api.post(FILMS_API, film);
        toast.success("Плёнка успешно добавлена!");
      }
      navigate("/films", { state: { shouldRefresh: true } });
    } catch (error) {
      console.error("Ошибка сохранения:", error);
      if (error.response?.status === 400) {
        setValidationErrors(error.response.data);
        toast.error("Пожалуйста, исправьте ошибки в форме");
      } else {
        toast.error("Ошибка сохранения данных!");
      }
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="container mt-4">
      <h2>{id ? `Редактирование плёнки #${id}` : "Добавление новой плёнки"}</h2>

      <form onSubmit={handleSubmit}>
        <div className="row g-3 mb-4">
          {/* Первая строка: 4 поля */}
          <div className="col-md-3">
            <label className="form-label">Название плёнки*</label>
            <input
              list="films-list"
              className={`form-control ${
                validationErrors.name ? "is-invalid" : ""
              }`}
              name="name"
              value={film.name}
              onChange={handleChange}
              required
            />
            <datalist id="films-list">
              {filmsList.map((f) => (
                <option key={f.film_id} value={f.film_name} />
              ))}
            </datalist>
            {validationErrors.name && (
              <div className="invalid-feedback">{validationErrors.name}</div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">ISO*</label>
            <input
              type="number"
              className={`form-control ${
                validationErrors.iso ? "is-invalid" : ""
              }`}
              name="iso"
              value={film.iso}
              onChange={handleChange}
              min="1"
              max="6400"
              step="1"
              required
            />
            {validationErrors.iso && (
              <div className="invalid-feedback">{validationErrors.iso}</div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Формат*</label>
            <select
              className={`form-select ${
                validationErrors.format ? "is-invalid" : ""
              }`}
              name="format"
              value={film.format}
              onChange={handleChange}
              required
            >
              <option disabled value="">Выберите</option>
              <option value="110">110</option>
              <option value="126">126</option>
              <option value="135">135 (35 мм)</option>
              <option value="120">120</option>
              <option value="220">220</option>
              <option value="sheet">Листовая</option>
            </select>
            {validationErrors.format && (
              <div className="invalid-feedback">{validationErrors.format}</div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Количество*</label>
            <input
              type="number"
              className={`form-control ${
                validationErrors.quantity ? "is-invalid" : ""
              }`}
              name="quantity"
              value={film.quantity}
              onChange={handleChange}
              min="1"
              required
            />
            {validationErrors.quantity && (
              <div className="invalid-feedback">
                {validationErrors.quantity}
              </div>
            )}
          </div>

          {/* Вторая строка: 4 поля */}
          <div className="col-md-3">
            <label className="form-label">Тип плёнки*</label>
            <select
              className={`form-select ${
                validationErrors.film_type ? "is-invalid" : ""
              }`}
              name="film_type"
              value={film.film_type}
              onChange={handleChange}
              required
            >
              <option value="negative">Негативная</option>
              <option value="reversal">Обращаемая</option>
              <option value="slide">Слайд</option>
            </select>
            {validationErrors.film_type && (
              <div className="invalid-feedback">
                {validationErrors.film_type}
              </div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Цветность*</label>
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn ${
                  film.color_type === "color" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setFilm(prev => ({ ...prev, color_type: "color" }))}
              >
                Цветная
              </button>
              <button
                type="button"
                className={`btn ${
                  film.color_type === "bw" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setFilm(prev => ({ ...prev, color_type: "bw" }))}
              >
                Ч/Б
              </button>
            </div>
            {validationErrors.color_type && (
              <div className="invalid-feedback d-block">
                {validationErrors.color_type}
              </div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Срок годности</label>
            <input
              type="date"
              className={`form-control ${
                validationErrors.expiry_date ? "is-invalid" : ""
              }`}
              name="expiry_date"
              value={film.expiry_date || ""}
              onChange={handleChange}
            />
            {validationErrors.expiry_date && (
              <div className="invalid-feedback">
                {validationErrors.expiry_date}
              </div>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Цена за катушку (руб)</label>
            <input
              type="number"
              className={`form-control ${
                validationErrors.price_per_roll ? "is-invalid" : ""
              }`}
              name="price_per_roll"
              value={film.price_per_roll || ""}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
            {validationErrors.price_per_roll && (
              <div className="invalid-feedback">
                {validationErrors.price_per_roll}
              </div>
            )}
          </div>

          {/* Третья строка: Примечания */}
          <div className="col-12">
            <label className="form-label">Примечания</label>
            <textarea
              className={`form-control ${
                validationErrors.notes ? "is-invalid" : ""
              }`}
              name="notes"
              value={film.notes}
              onChange={handleChange}
              rows="3"
            />
            {validationErrors.notes && (
              <div className="invalid-feedback">{validationErrors.notes}</div>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/films")}
          >
            <BiArrowBack className="me-1" /> Назад
          </button>

          <button type="submit" className="btn btn-primary">
            <BiSave className="me-1" /> {id ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </form>
      <ScrollToTopButton/>
    </div>
  );
};

export default FilmForm;